import * as z from "zod";
import { nanoid } from "nanoid";
import { zql } from "../generatedDrizzleSchema";
import { mutator } from "./mutator";

export const updateAIBudgetSchema = z.object({
  id: z.string().optional(),
  scopeKind: z.enum(["tenant", "ai_provider", "space"]),
  scopeId: z.string().optional(),
  aiProviderId: z.string().optional(),
  limitAmount: z.number().positive().optional(),
  warnThresholdPct: z.number().min(1).max(100).optional(),
  productivityHourlyRate: z.number().positive().optional(),
});

export const updateAIBudget = mutator({
  schema: updateAIBudgetSchema,
  resolve: {},
  permissions: [
    {
      permission: "tenant:ai_providers_manage",
      scopeId: ({ ctx }) => ctx.tenantId,
    },
  ],
  fn: async ({ tx, ctx, args }) => {
    const now = Date.now();
    const scopeId =
      args.scopeKind === "tenant"
        ? ctx.tenantId
        : (args.scopeId ??
          (args.scopeKind === "ai_provider" ? args.aiProviderId : undefined));
    if (!scopeId) throw new Error("scopeId required");
    if (args.limitAmount == null && args.productivityHourlyRate == null) {
      throw new Error("limitAmount or productivityHourlyRate required");
    }

    const aiProviderId =
      args.scopeKind === "space" ? (args.aiProviderId ?? null) : null;

    // Always resolve by the scope the caller was authorized for — never by a
    // caller-supplied id, which could name a budget in another scope. The
    // tenantId filter keeps the unscoped lookup (no RLS) from updating another
    // tenant's budget row for a foreign scopeId.
    const scoped = zql.aiBudgets
      .where("tenantId", ctx.tenantId)
      .where("scopeKind", args.scopeKind)
      .where("scopeId", scopeId);
    const existing = await tx.run(
      (aiProviderId === null
        ? scoped.where("aiProviderId", "IS", null)
        : scoped.where("aiProviderId", aiProviderId)
      ).one()
    );
    const values = {
      scopeKind: args.scopeKind,
      scopeId,
      aiProviderId,
      limitAmount: args.limitAmount ?? existing?.limitAmount ?? null,
      productivityHourlyRate:
        args.productivityHourlyRate ?? existing?.productivityHourlyRate ?? null,
      warnThresholdPct:
        args.warnThresholdPct ?? existing?.warnThresholdPct ?? 80,
      updatedAt: now,
    };
    if (existing) {
      await tx.mutate.aiBudgets.update({
        id: existing.id,
        ...values,
      });
      return;
    }
    await tx.mutate.aiBudgets.insert({
      id: args.id ?? nanoid(12),
      ...values,
      createdAt: now,
      tenantId: ctx.tenantId,
    });
  },
});
